import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TimeInputMeridiem } from './time-input-field.types';

@Component({
  selector: 'app-time-input-field',
  templateUrl: './time-input-field.component.html',
  styleUrls: ['./time-input-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeInputFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimeInputFieldComponent),
      multi: true,
    },
  ],
})
export class TimeInputFieldComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy
{
  @Input() label = '';
  @Input() required = false;
  @Input() disabled = false;

  /** `'ds'`: max-width 292 px (token DS). `'fill'`: ocupa 100% del padre. */
  @Input() widthLayout: 'ds' | 'fill' = 'ds';

  @HostBinding('class.fill') get _isFill(): boolean {
    return this.widthLayout === 'fill';
  }

  @Input() placeholder = '00:00';

  /** Tamaño de bloque en minutos (ej. 15 → 00/15/30/45). Debe dividir 60. */
  @Input() minuteStep = 15;

  /**
   * Mapa de claves de validador a mensaje de error.
   * Ejemplo: `{ required: 'Campo obligatorio', timeFormat: 'Formato inválido (HH:MM)' }`
   */
  @Input() errorMessages: Record<string, string> = {};

  /** Emite el valor combinado `"HH:MM AM"` / `"HH:MM PM"` cada vez que cambia. */
  @Output() valueChange = new EventEmitter<string>();

  /** Hora en formato 12 h (01–12). */
  readonly hourCtrl = new FormControl('');
  /** Minutos según `minuteStep` (00–59). */
  readonly minuteCtrl = new FormControl('');
  readonly meridiemCtrl = new FormControl('AM' as TimeInputMeridiem);

  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};
  private readonly _destroy$ = new Subject<void>();
  private _writing = false;

  constructor(private readonly _cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this._syncDisabled();

    this.hourCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._emitCombined());
    this.minuteCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._emitCombined());
    this.meridiemCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._emitCombined());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this._syncDisabled();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // --- ControlValueAccessor ---

  writeValue(value: string | null): void {
    this._writing = true;
    const parsed = this._parseCombined(value);
    this.hourCtrl.setValue(parsed.hour, { emitEvent: false });
    this.minuteCtrl.setValue(parsed.minute, { emitEvent: false });
    this.meridiemCtrl.setValue(parsed.meridiem, { emitEvent: false });
    this._writing = false;
    this._cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._syncDisabled();
    this._cdr.markForCheck();
  }

  // --- Validator ---

  validate(_: AbstractControl): ValidationErrors | null {
    const hh = String(this.hourCtrl.value ?? '').trim();
    const mm = String(this.minuteCtrl.value ?? '').trim();
    if (!hh || !mm) {
      return this.required ? { required: true } : null;
    }
    return this._isValidParts(hh, mm) ? null : { timeFormat: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.hourCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => fn());
    this.minuteCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => fn());
  }

  // --- Template helpers ---

  public get firstErrorMessage(): string | null {
    const errors = this._combinedErrors();
    if (!errors) return null;
    for (const key of Object.keys(errors)) {
      if (this.errorMessages[key]) return this.errorMessages[key];
    }
    if (errors['required']) return 'Campo requerido';
    if (errors['timeFormat']) return 'Formato inválido (ej. 08:30)';
    return null;
  }

  public onTimeTouched(): void {
    this._onTouched();
  }

  public get hourOptions(): string[] {
    // 12 h: 01–12
    return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  }

  public get minuteOptions(): string[] {
    const step = this._normalizedMinuteStep();
    const out: string[] = [];
    for (let m = 0; m < 60; m += step) {
      out.push(String(m).padStart(2, '0'));
    }
    return out;
  }

  // --- Private ---

  private _emitCombined(): void {
    if (this._writing) return;
    const hh = String(this.hourCtrl.value ?? '').trim();
    const mm = String(this.minuteCtrl.value ?? '').trim();
    const meridiem = (this.meridiemCtrl.value ?? 'AM') as TimeInputMeridiem;
    const combined = hh && mm ? `${hh}:${mm} ${meridiem}` : '';
    this._onChange(combined);
    this.valueChange.emit(combined);
  }

  private _syncDisabled(): void {
    if (this.disabled) {
      if (this.hourCtrl.enabled) this.hourCtrl.disable({ emitEvent: false });
      if (this.minuteCtrl.enabled) this.minuteCtrl.disable({ emitEvent: false });
      if (this.meridiemCtrl.enabled) this.meridiemCtrl.disable({ emitEvent: false });
    } else {
      if (this.hourCtrl.disabled) this.hourCtrl.enable({ emitEvent: false });
      if (this.minuteCtrl.disabled) this.minuteCtrl.enable({ emitEvent: false });
      if (this.meridiemCtrl.disabled) this.meridiemCtrl.enable({ emitEvent: false });
    }
  }

  private _parseCombined(value: string | null): { hour: string; minute: string; meridiem: TimeInputMeridiem } {
    const v = String(value ?? '').trim();
    const m = v.match(/^(\d{1,2}:\d{2})\s*(AM|PM)$/i);
    if (m) {
      const raw = m[1];
      const [hRaw, minRaw] = raw.split(':');
      const hour = this._normalizeHour(hRaw);
      const minute = this._normalizeMinute(minRaw);
      const meridiem: TimeInputMeridiem = m[2].toUpperCase() === 'PM' ? 'PM' : 'AM';
      return { hour, minute, meridiem };
    }
    // Si viene solo "HH:mm" (sin meridiano), intentamos mapear a selects.
    const mm = v.match(/^(\d{1,2}):(\d{2})$/);
    if (mm) {
      const hour = this._normalizeHour(mm[1]);
      const minute = this._normalizeMinute(mm[2]);
      return { hour, minute, meridiem: 'AM' };
    }
    return { hour: '', minute: '', meridiem: 'AM' };
  }

  private _isValidParts(hour: string, minute: string): boolean {
    const hh = Number(hour);
    const mm = Number(minute);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
    if (hh < 1 || hh > 12) return false;
    if (mm < 0 || mm > 59) return false;
    const step = this._normalizedMinuteStep();
    if (mm % step !== 0) return false;
    return true;
  }

  private _normalizeHour(value: string): string {
    const n = Number(String(value ?? '').trim());
    if (!Number.isFinite(n) || n < 1 || n > 12) return '';
    return String(n).padStart(2, '0');
  }

  private _normalizeMinute(value: string): string {
    const n = Number(String(value ?? '').trim());
    if (!Number.isFinite(n) || n < 0 || n > 59) return '';
    const step = this._normalizedMinuteStep();
    if (n % step !== 0) return '';
    return String(n).padStart(2, '0');
  }

  private _normalizedMinuteStep(): number {
    const n = Math.floor(Number(this.minuteStep));
    if (!Number.isFinite(n) || n <= 0) return 1;
    if (60 % n !== 0) return 1;
    return n;
  }

  private _combinedErrors(): ValidationErrors | null {
    const hh = String(this.hourCtrl.value ?? '').trim();
    const mm = String(this.minuteCtrl.value ?? '').trim();
    if (!hh || !mm) {
      return this.required ? { required: true } : null;
    }
    return this._isValidParts(hh, mm) ? null : { timeFormat: true };
  }
}
